<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">Material Orders</h2>
              <p class="text-body-2 grey--text ma-0">Track and manage all raw material orders</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined>
                <v-icon left small>mdi-truck-delivery</v-icon>
                {{ count }} Orders
              </v-chip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="3">
              <v-select v-model="filter.status" :items="statuses" label="Status" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <section v-if="isLoading" style="height: 300px" class="d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </section>

          <v-data-table v-else :headers="headers" :items="orders" hide-default-footer>
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium">{{ index + 1 }}</span>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip :color="statusColor(item.status)" small dark>{{ item.status }}</v-chip>
            </template>

            <template #[`item.user`]="{ item }">
              <div v-if="item.user">
                <div class="font-weight-medium">{{ item.user.first_name || '' }} {{ item.user.last_name || '' }}</div>
                <div class="text-caption grey--text">{{ item.user.phone || item.user.email || '' }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.plant`]="{ item }">
              <div v-if="item.plant">
                <div class="font-weight-medium">{{ item.plant.name }}</div>
                <div class="text-caption grey--text">{{ item.plant.city }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.material`]="{ item }">
              <div v-if="item.material">
                <div class="font-weight-medium">{{ item.material.name }}</div>
                <div class="text-caption grey--text">{{ item.material.brand }} - {{ item.material.grade }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.quantity`]="{ item }">
              {{ item.quantity }} {{ item.material ? item.material.unit : '' }}
            </template>

            <template #[`item.amount`]="{ item }">
              <span class="font-weight-medium">₹{{ formatNumber(item.total_amount) }}</span>
            </template>

            <template #[`item.date`]="{ item }">
              {{ item.created_at ? item.created_at.substring(0, 10) : '—' }}
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex align-center gap-1">
                <v-btn v-if="item.status === 'pending'" x-small color="success" class="mr-1" :loading="updatingId === item.id" @click="updateStatus(item, 'confirmed')">Confirm</v-btn>
                <v-btn v-if="item.status === 'confirmed'" x-small color="primary" class="mr-1" :loading="updatingId === item.id" @click="updateStatus(item, 'delivered')">Deliver</v-btn>
                <v-btn v-if="item.status !== 'delivered' && item.status !== 'cancelled'" x-small color="error" outlined :loading="updatingId === item.id" @click="updateStatus(item, 'cancelled')">Cancel</v-btn>
              </div>
            </template>

            <template #no-data>
              <div class="text-center py-12">
                <v-icon size="60" color="grey lighten-3">mdi-truck-delivery</v-icon>
                <h3 class="grey--text mt-4">No Orders Found</h3>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "RmcOrdersPage",
  data() {
    return {
      orders: [],
      count: 0,
      isLoading: false,
      updatingId: null,
      filter: { status: "" },
      statuses: ["pending", "confirmed", "delivered", "cancelled"],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px" },
        { text: "Status", value: "status", width: "110px" },
        { text: "Ordered By", value: "user", width: "160px" },
        { text: "Plant", value: "plant", width: "180px" },
        { text: "Material", value: "material", width: "200px" },
        { text: "Quantity", value: "quantity", width: "110px" },
        { text: "Amount", value: "amount", width: "120px" },
        { text: "Date", value: "date", width: "110px" },
        { text: "Actions", value: "actions", sortable: false, width: "200px" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filter.status) params.status = this.filter.status;
        const query = new URLSearchParams(params);
        const response = await axios.get(`rmc_material_orders?${query}`);
        this.orders = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (e) {
        console.error("Error fetching orders:", e);
      }
      this.isLoading = false;
    },
    async updateStatus(item, status) {
      this.updatingId = item.id;
      try {
        await axios.patch(`rmc_material_orders/${item.id}/status`, { status });
        item.status = status;
      } catch (e) {
        console.error("Error updating order status:", e);
      }
      this.updatingId = null;
    },
    statusColor(status) {
      const colors = { pending: "orange", confirmed: "info", delivered: "success", cancelled: "error" };
      return colors[status] || "grey";
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(num) || 0);
    },
  },
};
</script>
